import { FloorAssemblyType, type FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type {
  FloorAssemblyExteriorFinishLayer,
  FloorAssemblyFinishLayer,
  SourcedFloorAssemblyFinishLayerOperator,
  FloorAssemblyInteriorFinishLayer,
  FloorFinish,
} from "building-types/floors/floorFinishTypes";
import { FloorFinishType } from "building-types/floors/floorFinishTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type {
  ExteriorFloorMaterialOverride,
  InteriorFloorMaterialOverride,
} from "building-types/settings/floorMaterialSettings";
import type { Wall } from "building-types/walls/wallTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import { materialSrcFromPalette } from "doc/materialLookup";
import { makeXYPlane } from "doc/measurementUtils";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import type { Face } from "geom/brep";
import { faceNew } from "geom/brep";
import { DIST_EPSILON_SQUARED, DIST_Z_FIGHT } from "geom/core";
import { polygonSubtract, polygonUnion } from "geom/polygon";
import { polyLine2Area, polyLine2Copy, polyLine2HasNaN, polyLine2SetCCW, polyLine2SetCW } from "geom/polyLine2";
import { polyLine2New, type PolyLine2, type PolyLine2Polygon } from "geom/polyLine2Types";
import { roomOpenToBelow } from "../../utils/roomOpenToBelow";

function floorAssemblyInteriorFinishLayerNew(
  finishPerimeters: PolyLine2Polygon[],
  finishMaterial: InteriorFloorMaterialOverride,
  srcHandle?: DocEntityHandle,
  isFloorThreshold = false,
): FloorAssemblyInteriorFinishLayer {
  const outlines = sanitizePolygons(finishPerimeters);
  return { finishType: FloorFinishType.INTERIOR, outlines, material: finishMaterial, srcHandle, isFloorThreshold };
}

function floorAssemblyExteriorFinishLayerNew(
  finishPerimeters: PolyLine2Polygon[],
  finishMaterial: ExteriorFloorMaterialOverride,
  srcHandle?: DocEntityHandle,
  isFloorThreshold = false,
): FloorAssemblyExteriorFinishLayer {
  const outlines = sanitizePolygons(finishPerimeters);
  return { finishType: FloorFinishType.EXTERIOR, outlines, material: finishMaterial, srcHandle, isFloorThreshold };
}

function floorAssemblyDynamicFinishLayerNew(
  finishPerimeters: PolyLine2Polygon[],
  room: Room,
  srcHandle?: DocEntityHandle,
  isFloorThreshold = false,
): FloorAssemblyFinishLayer {
  return isInteriorRoomEnclosure[room.attributes.roomEnclosure]
    ? floorAssemblyInteriorFinishLayerNew(finishPerimeters, "floorInterior0", srcHandle, isFloorThreshold)
    : floorAssemblyExteriorFinishLayerNew(finishPerimeters, "deck0", srcHandle, isFloorThreshold);
}

export function makeRoomFloorFinishAssembly({
  room,
  roomOutlines,
  finishOperators,
}: {
  room: Room;
  roomOutlines: RoomOutlines;
  roomWalls: Wall[];
  finishOperators: SourcedFloorAssemblyFinishLayerOperator[];
}): FloorAssemblyFinishLayer[] {
  if (roomOpenToBelow(room) && room.attributes?.roomType !== RoomType.PORTICO) {
    return [];
  }

  const replaceLayers: FloorAssemblyFinishLayer[] = [];
  const addLayers: FloorAssemblyFinishLayer[] = [];

  for (const operator of finishOperators) {
    const finishLayer = makeFloorFinishOperatorFinishLayer(room, roomOutlines, operator);
    switch (operator.operationType) {
      case "ADD": {
        addLayers.push(finishLayer);
        break;
      }
      case "REPLACE_ALL": {
        replaceLayers.push(finishLayer);
        break;
      }
      default: {
        assertUnreachable(operator.operationType);
      }
    }
  }

  const replaceLayersCombined = mergeFloorFinishLayers(replaceLayers);

  const floorFinishLayers = [
    ...addLayers.map((l) => subtractFloorFinish(l, replaceLayersCombined.combinedMaterialZones)),
    ...replaceLayersCombined.mergedFinishLayers,
  ];

  return floorFinishLayers.filter((x) => x.outlines.length);
}

function makeFloorFinishOperatorFinishLayer(
  room: Room,
  roomOutlines: RoomOutlines,
  floorFinishOperator: SourcedFloorAssemblyFinishLayerOperator,
): FloorAssemblyFinishLayer {
  const { finishPerimeter, isFloorThreshold, srcHandle } = floorFinishOperator;
  let perimeter: PolyLine2 = finishPerimeter ?? polyLine2New([]);

  if (perimeter.length === 0 && roomOutlines.INSIDE_OF_MASS_IGNORE_VOID) {
    perimeter = polyLine2Copy(roomOutlines.INSIDE_OF_MASS_IGNORE_VOID);
  }

  switch (floorFinishOperator.finishType) {
    case FloorFinishType.INTERIOR: {
      return floorAssemblyInteriorFinishLayerNew(
        [[perimeter]],
        floorFinishOperator.finishMaterial,
        srcHandle,
        isFloorThreshold,
      );
    }
    case FloorFinishType.EXTERIOR: {
      return floorAssemblyExteriorFinishLayerNew(
        [[perimeter]],
        floorFinishOperator.finishMaterial,
        srcHandle,
        isFloorThreshold,
      );
    }
    case FloorFinishType.DYNAMIC: {
      return floorAssemblyDynamicFinishLayerNew([[perimeter]], room, srcHandle, isFloorThreshold);
    }
    default: {
      assertUnreachable(floorFinishOperator);
    }
  }
}

/*
 * @TODO - DO NOT union floor finishes with the same material any more?
 */
function mergeFloorFinishLayers(floorFinishLayers: FloorAssemblyFinishLayer[]) {
  const materialZones: PolyLine2Polygon[] = [];
  const materialsZonesByType: { [material: string]: FloorAssemblyFinishLayer } = {};

  for (const layout of floorFinishLayers) {
    const { material, outlines, finishType, srcHandle } = layout;
    if (!material) {
      continue;
    }
    if (!materialsZonesByType[material]) {
      materialsZonesByType[material] =
        finishType === FloorFinishType.INTERIOR
          ? floorAssemblyInteriorFinishLayerNew([], material, srcHandle)
          : floorAssemblyExteriorFinishLayerNew([], material, srcHandle);
    }
    materialsZonesByType[material].outlines.push(...outlines);
  }

  for (const material in materialsZonesByType) {
    const { outlines } = materialsZonesByType[material];
    if (!outlines?.length) {
      continue;
    }
    const zones: PolyLine2Polygon[] = [];
    if (outlines.length > 1) {
      const zoneUnion = polygonUnion(outlines);
      zones.push(...zoneUnion);
      materialZones.push(...zoneUnion);
    } else {
      zones.push(...outlines);
      materialZones.push(...outlines);
    }
    const outlinesSanitized = sanitizePolygons(zones);
    materialsZonesByType[material] = { ...materialsZonesByType[material], outlines: outlinesSanitized };
  }

  return {
    mergedFinishLayers: Object.values(materialsZonesByType),
    combinedMaterialZones: materialZones,
  };
}

function subtractFloorFinish(layout: FloorAssemblyFinishLayer, sub: PolyLine2Polygon[]): FloorAssemblyFinishLayer {
  const { outlines } = layout;

  const subtracted: PolyLine2Polygon[] = [];
  for (const outline of outlines) {
    const subtractedOutlines = polygonSubtract([outline, ...sub]);
    sanitizePolygons(subtractedOutlines);
    subtracted.push(...subtractedOutlines);
  }
  return { ...layout, outlines: subtracted };
}

function sanitizePolygons(pgs: PolyLine2Polygon[]) {
  const sanitizedOutlines: PolyLine2Polygon[] = [];
  for (const polygon of pgs) {
    if (!polygon) {
      continue;
    }
    let polygonInvalid = false;
    for (const loop of polygon) {
      if (!loop) {
        polygonInvalid = true;
        break;
      }
      if (polyLine2HasNaN(loop)) {
        polygonInvalid = true;
        break;
      }
      if (polyLine2Area(loop) < DIST_EPSILON_SQUARED) {
        polygonInvalid = true;
        break;
      }
    }

    if (polygonInvalid) {
      continue;
    }
    sanitizedOutlines.push(polygon);
  }

  for (const pg of sanitizedOutlines) {
    polyLine2SetCW(pg[0]);
    for (let i = 1; i < pg.length; i++) {
      polyLine2SetCCW(pg[i]);
    }
  }

  return sanitizedOutlines;
}

export function makeRoomFloorFinishes(
  room: Room,
  floorStructuralAssembly: FloorAssembly,
  floorFinishAssembly: FloorAssemblyFinishLayer[],
  interiorPalette: InteriorPalette,
  exteriorPalette: ExteriorPalette,
): FloorFinish[] {
  if (floorStructuralAssembly.assemblyType === FloorAssemblyType.VOID) {
    return [];
  }
  //TODO: Placeholder for finish thickness
  const floorElevation = floorAssemblyTop(floorStructuralAssembly, room.level);
  const floorFinishes: FloorFinish[] = [];
  const finishPlane = makeXYPlane(0, 0, floorElevation + DIST_Z_FIGHT);

  for (const layout of floorFinishAssembly) {
    const { outlines, isFloorThreshold, finishType, srcHandle } = layout;

    const { materialSlot, materialSrc } = roomFloorFinishLayouMaterial(room, layout, interiorPalette, exteriorPalette);
    if (!materialSrc || !materialSlot) {
      continue;
    }

    for (const outline of outlines) {
      const face = faceNew(outline, finishPlane);
      if (!face) {
        continue;
      }

      floorFinishes.push(floorFinishNew(finishType, face, materialSlot, materialSrc, srcHandle, isFloorThreshold));
    }
  }

  return floorFinishes;
}

function roomFloorFinishLayouMaterial(
  room: Room,
  roomFloorFinishLayout: FloorAssemblyFinishLayer,
  interiorPalette: InteriorPalette,
  exteriorPalette: ExteriorPalette,
): { materialSlot?: PaletteMaterialSlot; materialSrc?: MaterialSrc } {
  let materialSlot: PaletteMaterialSlot | undefined;
  let materialSrc: MaterialSrc | undefined;
  if (roomFloorFinishLayout.material === "NONE") {
    return { materialSlot, materialSrc };
  }

  if (roomFloorFinishLayout.finishType === FloorFinishType.INTERIOR) {
    materialSlot = roomFloorFinishLayout.material;
    materialSrc = materialSrcFromPalette(interiorPalette, materialSlot, room.type);
  } else if (roomFloorFinishLayout.finishType === FloorFinishType.EXTERIOR) {
    materialSlot = roomFloorFinishLayout.material;
    materialSrc = materialSrcFromPalette(exteriorPalette, materialSlot);
  }

  return {
    materialSlot,
    materialSrc,
  };
}

export function floorFinishNew(
  finishType: FloorFinishType.INTERIOR | FloorFinishType.EXTERIOR,
  finishFace: Face,
  materialSlot: PaletteMaterialSlot,
  materialSrc: MaterialSrc,
  srcHandle?: DocEntityHandle,
  threshold?: boolean,
): FloorFinish {
  return {
    finishType,
    finishFace,
    materialSlot,
    materialSrc,
    srcHandle,
    isFloorTheshold: threshold,
  };
}
